import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { DragSourceDataContextType } from "shared/context/drag-and-drop/DragSourceDataContext"
import {
  PointerPosition,
  PointerPositionContextType
} from "shared/context/drag-and-drop/PointerPositionContext"
import { getWindow } from "shared/lib/getWindow"
import { useThrottle } from "shared/lib/performance/useThrottle"
import { useEvent } from "shared/lib/useEvent"
import { DragSource, isDragSource } from "shared/types/drag-and-drop/DragSource"
import { DropTarget, isDropTarget } from "shared/types/drag-and-drop/DropTarget"
import { getAcceptAwarePointerWithin } from "shared/ui/table/dn-d/collisions/acceptAwarePointerWithin"

import {
  CollisionDetection,
  DragMoveEvent,
  DragStartEvent,
  Modifier,
  MouseSensor,
  SensorDescriptor,
  TouchSensor,
  useSensor
} from "@dnd-kit/core"

import { calculatePointerPosition } from "./calculatePointerPosition"

export interface UseDnDContextStateOptions {
  modifiers?: Modifier[]
}

export interface UseDnDContextStateReturn {
  activeDragSource: DragSource | null
  activeDropTarget: DropTarget | null
  handleDragStart: (event: DragStartEvent) => void
  handleDragMove: (event: DragMoveEvent) => void
  handleDragEnd: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sensors: SensorDescriptor<any>[]
  collisionDetection: CollisionDetection
  dragSourceContext: DragSourceDataContextType
  pointerPositionContext: PointerPositionContextType
  modifiers?: Modifier[]
}

export function useDnDContextState(
  options: UseDnDContextStateOptions = {}
): UseDnDContextStateReturn {
  const { modifiers } = options

  const [activeDragSource, setActiveDragSource] = useState<DragSource | null>(
    null
  )
  const [activeDropTarget, setActiveDropTarget] = useState<DropTarget | null>(
    null
  )

  const pointerPositionRef = useRef<PointerPosition | null>(null)
  const getPointerPosition = useEvent(() => pointerPositionRef.current)

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (isDragSource(event.active.data.current)) {
      setActiveDragSource(event.active.data.current)
    }
  }, [])

  // Separate throttled pointer position update
  const updatePointerPosition = useCallback(
    (event: PointerEvent | MouseEvent | TouchEvent) => {
      pointerPositionRef.current = calculatePointerPosition(event)
    },
    []
  )

  const throttledUpdatePointerPosition = useThrottle(updatePointerPosition, 100)

  const handleDragMoveUnthrottled = useCallback(
    (event: DragMoveEvent) => {
      if (isDropTarget(event.over?.data.current)) {
        setActiveDropTarget(event.over?.data.current)
      } else {
        setActiveDropTarget(null)
      }
    },
    [throttledUpdatePointerPosition]
  )

  const handleDragMove = useThrottle(handleDragMoveUnthrottled)

  const handleDragEnd = useCallback(() => {
    setActiveDragSource(null)
    setActiveDropTarget(null)
    pointerPositionRef.current = null
  }, [])

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10
    }
  })

  // `tolerance` is mandatory once `delay` is set: dnd-kit's handleMove passes it
  // straight into `'x' in measurement`, which throws on touch devices when omitted.
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 10
    }
  })

  const collisionDetection = useMemo(
    () =>
      getAcceptAwarePointerWithin(
        activeDragSource?.type,
        activeDragSource?.origin
      ),
    [activeDragSource]
  )

  const dragSourceContext = useMemo<DragSourceDataContextType>(
    () => ({ dragSource: activeDragSource }),
    [activeDragSource]
  )

  // Context value with stable function (useEvent ensures no re-renders)
  const pointerPositionContext: PointerPositionContextType = {
    getPointerPosition
  }

  useEffect(() => {
    if (activeDragSource) {
      getWindow()?.addEventListener("pointermove", updatePointerPosition)
      return () =>
        getWindow()?.removeEventListener("pointermove", updatePointerPosition)
    }
    return undefined
  }, [activeDragSource])

  return {
    activeDragSource,
    activeDropTarget,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    sensors: [mouseSensor, touchSensor],
    collisionDetection,
    dragSourceContext,
    pointerPositionContext,
    modifiers
  }
}
